
import { useTranslation } from 'react-i18next';
import { useState, ChangeEvent } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  CardTitle, 
  CardDescription 
} from "@/components/ui/card";
import { useRestaurant } from "@/contexts/RestaurantContext";
import { useAuth } from "@/contexts/AuthContext";
import { useToast } from "@/hooks/use-toast";
import { Badge, badgeVariants } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { 
  Building2, 
  MapPin, 
  FileDigit, 
  CalendarClock, 
  Phone,
  Mail,
  Globe,
  Hash,
  Flag,
  Clock,
  CheckCircle2,
  XCircle,
  PlusCircle,
  User,
  BarChart,
  Link2
} from "lucide-react";
import { RestaurantProfile as RestaurantProfileType } from '@/services/api/restaurant';

export const RestaurantProfile = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const { restaurantProfile, setRestaurantProfile, isLoading, error } = useRestaurant();
  const { user } = useAuth();
  const [isSaving, setIsSaving] = useState(false);
  const [formData, setFormData] = useState<RestaurantProfileType>(() => {
    if (restaurantProfile) {
      return { ...restaurantProfile };
    }
    
    // Default values for a new restaurant
    return {
      id: Math.floor(Math.random() * 10000),
      client: 1,
      name: '',
      description: '',
      cif: '',
      address: '',
      cp: '',
      poblacion: '',
      provincia: '',
      telefono: '',
      paginaweb: '',
      email: '',
      active: 1,
      user_id: user?.uid || '',
      analytics: 0,
      slug: '',
      municipio: '',
      pais: '',
      blocked: 0,
      creation_date: new Date().toISOString(),
      last_update: new Date().toISOString(),
      business_unit_id: 1,
      business_unit_name: 'Default Business Unit'
    };
  });

  const handleChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleToggleChange = (name: string, value: number) => {
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  if (isLoading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900" />
          </div>
        </CardContent>
      </Card>
    );
  }

  if (error) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="text-red-500">
            {error.message}
          </div>
        </CardContent>
      </Card>
    );
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSaving(true);

    if (!user?.uid) {
      toast({
        title: t('settings.error'),
        description: t('settings.notAuthenticated'),
        variant: 'destructive',
      });
      setIsSaving(false);
      return;
    }

    try {
      if (restaurantProfile) {
        // Update existing profile with form data
        await setRestaurantProfile({
          ...formData,
          user_id: user.uid,
          last_update: new Date().toISOString(),
        });
      } else {
        // Create new profile
        const slug = formData.name.toLowerCase().replace(/\s+/g, '-');
        await setRestaurantProfile({
          ...formData,
          user_id: user.uid,
          slug,
          creation_date: new Date().toISOString(),
          last_update: new Date().toISOString(),
        });
      }
      
      toast({
        title: t('settings.success'),
        description: restaurantProfile 
          ? t('settings.profileUpdated') 
          : t('settings.profileCreated'),
      });
    } catch (err) {
      toast({
        title: t('settings.error'),
        description: t('settings.updateFailed'),
        variant: 'destructive',
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Determine if we are creating a new restaurant or editing an existing one
  const isCreating = !restaurantProfile;

  return (
    <Card as="form" onSubmit={handleSubmit}>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="flex items-center gap-2">
            <Building2 className="h-5 w-5" />
            {isCreating ? t('settings.createRestaurant') : t('settings.restaurantDetails')}
          </CardTitle>
          {!isCreating && (
            <div className={cn(
              badgeVariants({ variant: formData.active ? "success" : "destructive" }),
              "ml-2"
            )}>
              {formData.active ? t('settings.active') : t('settings.inactive')}
            </div>
          )}
        </div>
        <CardDescription>
          {isCreating 
            ? t('settings.setupNewRestaurant') 
            : t('settings.manageRestaurantInfo')}
        </CardDescription>
      </CardHeader>
      <CardContent className="space-y-6">
        {/* Basic information - IDs section - only shown for existing restaurants */}
        {!isCreating && (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="id">{t('settings.restaurantId')}</Label>
              <div className="flex items-center space-x-2">
                <Hash className="h-4 w-4 text-muted-foreground" />
                <Input id="id" name="id" value={formData.id} readOnly />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="client">{t('settings.clientId')}</Label>
              <div className="flex items-center space-x-2">
                <User className="h-4 w-4 text-muted-foreground" />
                <Input id="client" name="client" value={formData.client} readOnly />
              </div>
            </div>
          </div>
        )}

        {/* Name and CIF */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label htmlFor="name">{t('settings.restaurantName')}</Label>
            <Input 
              name="name" 
              id="name" 
              value={formData.name}
              onChange={handleChange}
              required
              placeholder={isCreating ? "Your Restaurant Name" : undefined}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="cif">{t('settings.cif')}</Label>
            <div className="flex items-center space-x-2">
              <FileDigit className="h-4 w-4 text-muted-foreground" />
              <Input 
                name="cif" 
                id="cif" 
                value={formData.cif}
                onChange={handleChange}
                required
                placeholder={isCreating ? "Business Identification Number" : undefined}
              />
            </div>
          </div>
        </div>

        {/* Description */}
        <div className="space-y-2">
          <Label htmlFor="description">{t('settings.description')}</Label>
          <Textarea 
            id="description" 
            rows={3} 
            name="description"
            value={formData.description || ""}
            onChange={handleChange}
            placeholder={isCreating ? "Describe your restaurant in a few sentences..." : undefined}
          />
        </div>

        {/* Address information */}
        <div className="space-y-2">
          <Label htmlFor="address">{t('settings.streetAddress')}</Label>
          <div className="flex items-center space-x-2">
            <MapPin className="h-4 w-4 text-muted-foreground" />
            <Input 
              name="address" 
              id="address" 
              value={formData.address}
              onChange={handleChange}
              required
              placeholder={isCreating ? "Street Address" : undefined}
            />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <div className="space-y-2">
            <Label htmlFor="cp">{t('settings.zipCode')}</Label>
            <Input 
              name="cp" 
              id="cp" 
              value={formData.cp}
              onChange={handleChange}
              required
              placeholder={isCreating ? "Zip Code" : undefined}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="municipio">{t('settings.city')}</Label>
            <Input 
              name="municipio" 
              id="municipio" 
              value={formData.municipio}
              onChange={handleChange}
              required
              placeholder={isCreating ? "City" : undefined}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="provincia">{t('settings.province')}</Label>
            <Input 
              name="provincia" 
              id="provincia" 
              value={formData.provincia}
              onChange={handleChange}
              required
              placeholder={isCreating ? "Province/State" : undefined}
            />
          </div>
          <div className="space-y-2">
            <Label htmlFor="pais">{t('settings.country')}</Label>
            <div className="flex items-center space-x-2">
              <Flag className="h-4 w-4 text-muted-foreground" />
              <Input 
                name="pais" 
                id="pais" 
                value={formData.pais}
                onChange={handleChange}
                required
                placeholder={isCreating ? "Country" : undefined}
              />
            </div>
          </div>
        </div>

        {/* Poblacion field */}
        <div className="space-y-2">
          <Label htmlFor="poblacion">{t('settings.poblacion') || "Population"}</Label>
          <Input 
            name="poblacion" 
            id="poblacion" 
            value={formData.poblacion}
            onChange={handleChange}
            required
            placeholder={isCreating ? "Population" : undefined}
          />
        </div>

        {/* Contact information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="telefono">{t('settings.businessPhone')}</Label>
            <div className="flex items-center space-x-2">
              <Phone className="h-4 w-4 text-muted-foreground" />
              <Input 
                name="telefono" 
                id="telefono" 
                value={formData.telefono}
                onChange={handleChange}
                required
                placeholder={isCreating ? "Business Phone" : undefined}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="email">{t('settings.businessEmail')}</Label>
            <div className="flex items-center space-x-2">
              <Mail className="h-4 w-4 text-muted-foreground" />
              <Input 
                name="email" 
                id="email" 
                type="email" 
                value={formData.email}
                onChange={handleChange}
                required
                placeholder={isCreating ? "Business Email" : undefined}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="paginaweb">{t('settings.website')}</Label>
            <div className="flex items-center space-x-2">
              <Globe className="h-4 w-4 text-muted-foreground" />
              <Input 
                name="paginaweb" 
                id="paginaweb" 
                value={formData.paginaweb || ""}
                onChange={handleChange}
                placeholder={isCreating ? "https://yourrestaurant.com" : undefined}
              />
            </div>
          </div>
        </div>

        {/* User and Analytics/Business Information */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="space-y-2">
            <Label htmlFor="user_id">{t('settings.userId') || "User ID"}</Label>
            <div className="flex items-center space-x-2">
              <User className="h-4 w-4 text-muted-foreground" />
              <Input 
                id="user_id" 
                name="user_id"
                value={formData.user_id} 
                onChange={handleChange}
                readOnly={!isCreating}
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="analytics">{t('settings.analytics') || "Analytics"}</Label>
            <div className="flex items-center space-x-2">
              <BarChart className="h-4 w-4 text-muted-foreground" />
              <Input 
                id="analytics" 
                name="analytics"
                value={formData.analytics} 
                onChange={handleChange}
                type="number"
                min="0"
                max="1"
              />
            </div>
          </div>
          <div className="space-y-2">
            <Label htmlFor="business_unit_id">{t('settings.businessUnitId') || "Business Unit ID"}</Label>
            <Input 
              id="business_unit_id" 
              name="business_unit_id"
              value={formData.business_unit_id} 
              onChange={handleChange}
              readOnly={!isCreating}
            />
          </div>
        </div>

        <div className="space-y-2">
          <Label htmlFor="business_unit_name">{t('settings.businessUnitName') || "Business Unit Name"}</Label>
          <Input 
            id="business_unit_name" 
            name="business_unit_name"
            value={formData.business_unit_name}
            onChange={handleChange}
            readOnly={!isCreating}
          />
        </div>

        {/* Status information - only shown for existing restaurants */}
        {!isCreating && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="space-y-2">
              <Label htmlFor="slug">{t('settings.slug')}</Label>
              <div className="flex items-center space-x-2">
                <Link2 className="h-4 w-4 text-muted-foreground" />
                <Input 
                  id="slug" 
                  name="slug"
                  value={formData.slug} 
                  onChange={handleChange}
                  readOnly 
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="creation_date">{t('settings.creationDate')}</Label>
              <div className="flex items-center space-x-2">
                <CalendarClock className="h-4 w-4 text-muted-foreground" />
                <Input 
                  id="creation_date" 
                  name="creation_date"
                  value={new Date(formData.creation_date).toLocaleDateString()} 
                  readOnly 
                />
              </div>
            </div>
            <div className="space-y-2">
              <Label htmlFor="last_update">{t('settings.lastUpdate')}</Label>
              <div className="flex items-center space-x-2">
                <Clock className="h-4 w-4 text-muted-foreground" />
                <Input 
                  id="last_update" 
                  name="last_update"
                  value={new Date(formData.last_update).toLocaleDateString()} 
                  readOnly 
                />
              </div>
            </div>
          </div>
        )}

        {/* Status flags */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center gap-2 p-3 border rounded-md">
            <div className="flex items-center gap-2">
              {formData.active ? (
                <CheckCircle2 className="h-5 w-5 text-green-500" />
              ) : (
                <XCircle className="h-5 w-5 text-red-500" />
              )}
              <Label className="mb-0">{t('settings.activeStatus')}</Label>
            </div>
            <div className="ml-auto">
              <Input 
                type="number" 
                name="active"
                min="0" 
                max="1" 
                value={formData.active}
                onChange={handleChange}
                className="w-16"
              />
            </div>
          </div>
          <div className="flex items-center gap-2 p-3 border rounded-md">
            <div className="flex items-center gap-2">
              {formData.blocked ? (
                <CheckCircle2 className="h-5 w-5 text-red-500" />
              ) : (
                <XCircle className="h-5 w-5 text-green-500" />
              )}
              <Label className="mb-0">{t('settings.blockedStatus')}</Label>
            </div>
            <div className="ml-auto">
              <Input 
                type="number" 
                name="blocked"
                min="0" 
                max="1" 
                value={formData.blocked}
                onChange={handleChange}
                className="w-16"
              />
            </div>
          </div>
        </div>

        <div className="flex justify-end">
          <Button onClick={handleSubmit} type="submit" disabled={isSaving} className="gap-2">
            {isCreating && <PlusCircle className="h-4 w-4" />}
            {isSaving 
              ? t('settings.saving') 
              : isCreating 
                ? t('settings.createRestaurant') 
                : t('settings.saveChanges')
            }
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};
